import React, {
  useCallback, useContext, useMemo, useState
} from 'react';
import { useNavigate } from 'react-router-dom';

import {
  ButtonBar, ErrorMessage,
  RadioCardOptionGroup,
  ScreenHeader
} from '../../components';
import { useCreateOrUpdate, useFormData } from '../../hooks';

import { NewFAContext } from '.';

const formConfig = {
  fields: [
    { 
      id: 'obituary-preference', 
      fieldName: 'obituary_preference',
      type: 'text' as const,
      required: true 
    }
  ]
};


const options = [
  {
    id: 'yes',
    label: 'Yes, publish my obituary',
    description: 'I want my obituary to be written.',
    name: 'obituary',
    icon: 'celebration',
    value: 'yes'
  },
  {
    id: 'no',
    label: 'No, I do not want an obituary',
    description: 'I do not want my obituary to be written.',
    name: 'obituary',
    icon: 'cancel',
    value: 'no'
  },
  {
    id: 'unsure',
    label: 'Unsure',
    description: 'I am not sure about having an obituary. Let my family decide.',
    name: 'obituary',
    icon: 'help_outline',
    value: 'unsure'
  }
];

export function NewFAObituaryPreferences() {
  const navigate = useNavigate();
  const { funeralArrangement, update } = useContext(NewFAContext);
  const { handleCreateOrUpdate } = useCreateOrUpdate('funeral_arrangements');
  const { handleData, errors } = useFormData(formConfig);

  const [ error, setError ] = useState<string | null>(null);
    
  const handleBack = useCallback(() => {
    navigate(-1);
  }, [ navigate ]);

  const handleNext = useCallback(async () => {
    const selectedElement = document.querySelector('input[name="obituary"]:checked');

    if (!selectedElement) {
      setError('Please select an obituary preference.');
      return;
    }

    const selectedOption = (selectedElement as HTMLInputElement).value;
    
    // Save the obituary preference
    const payload = {
      obituary_preference: selectedOption,
      user_id: funeralArrangement?.user_id || null,
      person_id: funeralArrangement?.person_id || null
    };

    try {
      await handleCreateOrUpdate(funeralArrangement?.id || '', payload);
      await update();
    } catch (err) {
      console.error('Error saving obituary preference:', err);
      setError('Failed to save preference. Please try again.');
      return;
    }

    // Navigate based on selection
    if (selectedOption === 'yes') {
      navigate('../obituary-details');
    } else {
      navigate('../financial-planning');
    }
  }, [ navigate, funeralArrangement, handleCreateOrUpdate, update ]);

  const memoizedOptions = useMemo(() => options, []);

  // IDEA: AI obituary writing tool
  return (
    <div className="new-fa-obituary-preferences screen">
      <ScreenHeader 
        title="Obituary Preferences"
        description="Do you want an obituary to be written?"
        steps={6}
        step={1}
        handleBack={handleBack}
      />
      
      <RadioCardOptionGroup options={memoizedOptions} />
      <ErrorMessage message={error || errors[0]} />
      
      <ButtonBar
        handlePrimary={handleNext}
      />
    </div>
  );
}